import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Flashlight, FlashlightOff, Keyboard } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { useAuthStore } from '@/stores/authStore';
import { isImeComposing, normalizeTypedCode } from '@tendedloop/shared/i18n';

export default function QRScanner() {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const hasNavigated = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated || showManualEntry) return;
    const scanner = new Html5Qrcode('qr-reader');
    scannerRef.current = scanner;

    scanner
      .start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
        },
        (decodedText) => {
          if (hasNavigated.current) return;
          hasNavigated.current = true;

          // Extract QR code from URL or use as-is
          let qrCode = decodedText;
          try {
            const url = new URL(decodedText);
            // Get the last path segment as the QR code
            const segments = url.pathname.split('/').filter(Boolean);
            const lastSegment = segments[segments.length - 1];
            if (lastSegment) {
              qrCode = lastSegment;
            }
          } catch {
            // Not a URL, use as-is
          }

          scanner.stop().catch(() => {});
          navigate(`/scan/${encodeURIComponent(qrCode)}`);
        },
        () => {
          // QR code detection failed, ignore (keeps scanning)
        },
      )
      .catch((err) => {
        const msg = err?.message || '';
        if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
          setError(t('scanner.cameraPermissionDenied'));
        } else {
          setError(t('scanner.cameraError'));
        }
      });

    return () => {
      if (scanner.isScanning) {
        scanner.stop().catch(() => {});
      }
    };
  }, [navigate, t, showManualEntry]);

  const toggleTorch = async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    try {
      const track = (scanner as unknown as { _localMediaStream?: MediaStream })._localMediaStream?.getVideoTracks()[0];
      if (track) {
        await track.applyConstraints({
          advanced: [{ torch: !torchOn } as MediaTrackConstraintSet],
        });
        setTorchOn(!torchOn);
      }
    } catch {
      // Torch not supported on this device
    }
  };

  const handleManualSubmit = () => {
    // Full-width letters or ー typed with a Japanese IME still reach the right code.
    const code = normalizeTypedCode(manualCode);
    if (!code) return;
    navigator.vibrate?.(50);
    navigate(`/scan/${encodeURIComponent(code)}`);
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/80 z-10">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/10 text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-white font-medium">{t('scanner.title')}</h1>
        <div className="flex gap-2">
          <button
            onClick={() => {
              // Stop camera when switching to manual
              if (scannerRef.current?.isScanning) {
                scannerRef.current.stop().catch(() => {});
              }
              setShowManualEntry(!showManualEntry);
              setError(null);
            }}
            className="p-2 rounded-full bg-white/10 text-white"
            aria-label={t('scanner.manualEntry')}
          >
            <Keyboard className="w-5 h-5" />
          </button>
          {!showManualEntry && (
            <button
              onClick={() => {
                navigator.vibrate?.(50);
                toggleTorch();
              }}
              className="p-2 rounded-full bg-white/10 text-white"
              aria-label={t('scanner.torch')}
            >
              {torchOn ? <FlashlightOff className="w-5 h-5" /> : <Flashlight className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>

      {/* Scanner or Manual Entry */}
      <div className="flex-1 flex items-center justify-center relative">
        {showManualEntry ? (
          <div className="w-full max-w-sm px-6">
            <p className="text-sm text-white/70 text-center mb-4">{t('scanner.orScanBelow')}</p>
            <div className="flex gap-2">
              <label className="sr-only" htmlFor="qr-manual">
                {t('scanner.manualEntry')}
              </label>
              <input
                id="qr-manual"
                type="text"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !isImeComposing(e) && handleManualSubmit()}
                placeholder={t('scanner.manualPlaceholder')}
                className="flex-1 px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-white/20 focus:border-white/50 focus:outline-none text-center text-lg tracking-wider"
                autoFocus
                autoCapitalize="characters"
              />
              <button
                onClick={handleManualSubmit}
                disabled={!manualCode.trim()}
                className="px-5 py-3 rounded-lg bg-forest text-white font-medium disabled:opacity-40"
              >
                {t('scanner.manualSubmit')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div id="qr-reader" className="w-full max-w-sm" />
            {error && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                <div className="text-center text-white px-6">
                  <p className="text-sm mb-4">{error}</p>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => {
                        setShowManualEntry(true);
                        setError(null);
                      }}
                      className="px-4 py-2 rounded-lg bg-forest/80 text-sm font-medium"
                    >
                      {t('scanner.manualEntry')}
                    </button>
                    <button onClick={() => navigate(-1)} className="px-4 py-2 rounded-lg bg-white/20 text-sm">
                      {t('scanner.goBack')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Instructions */}
      <div className="px-4 py-4 bg-black/80 text-center">
        <p className="text-sm text-white/70">
          {showManualEntry ? t('scanner.orScanBelow') : t('scanner.instructions')}
        </p>
      </div>
    </div>
  );
}
